import { Injectable, Inject, OnDestroy } from '@angular/core';
import { Permissions } from '@app/modules/authentication/store/auth';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { ValueFormatterParams } from 'ag-grid-enterprise';
import { getCellFilter, getCellRenderer, getCellSort, getValueGetter } from 'src/app/modules/core/components/ag-grid/ag-grid-utils';
import {
  FieldDataType,
  FilterGroup,
  FilterGroupType,
  RuleOperator,
  SchemaField,
  SystemFieldType,
  View,
  Workflow
} from 'src/app/modules/core/store/models';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class TableColumnsService {
  private optionsToRuleOperators: { option: string; operator: RuleOperator }[] = [
    { option: 'equals', operator: RuleOperator.Equal },
    { option: 'notEqual', operator: RuleOperator.NotEqual },
    { option: 'equalsNoCase', operator: RuleOperator.EqualNoCase },
    { option: 'notEqualNoCase', operator: RuleOperator.NotEqualNoCase },
    { option: 'contains', operator: RuleOperator.ContainsNoCase },
    { option: 'notContains', operator: RuleOperator.DoesNotContainNoCase },
    { option: 'startsWith', operator: RuleOperator.ContainsNoCase },
    { option: 'endsWith', operator: RuleOperator.ContainsNoCase },
    { option: 'lessThan', operator: RuleOperator.LessThan },
    { option: 'lessThanOrEqual', operator: RuleOperator.LessThanEqualTo },
    { option: 'greaterThan', operator: RuleOperator.GreaterThan },
    { option: 'greaterThanOrEqual', operator: RuleOperator.GreaterThanEqualTo },
    { option: 'inRange', operator: RuleOperator.DateIsNotWithinLast },
    { option: 'blank', operator: RuleOperator.IsEmpty },
    { option: 'notBlank', operator: RuleOperator.IsNotEmpty },
    { option: 'empty	', operator: RuleOperator.IsEmpty }
  ];

  constructor(private userContextService: UserContextService) {}

  getDatasetColumns(dataset: Dataset, organizationUId: string, setDistanceColumn: boolean, skipLinkChannels?: boolean) {
    const fieldNames = [];

    if (!!!dataset) {
      return [];
    }

    const tableView = dataset?.workflow?.views?.find(vw => vw.name === 'table');

    tableView?.controls.map(c => {
      const field = dataset.schema.fields.find(f => f.uId === c.dataSetFieldUId);
      if (!!field) {
        fieldNames.push(`${field.systemName}`);
      }
    });

    return this.generateTableColumns(fieldNames, dataset, dataset.workflow, organizationUId, setDistanceColumn, skipLinkChannels);
  }

  generateTableColumns(
    fieldNames: string[],
    dataset: Dataset,
    workflow: Workflow,
    organizationUId: string,
    setDistanceColumn: boolean,
    skipLinkChannels?: boolean
  ) {
    const columns = [];

    var userContext = this.userContextService.getUserContext();

    fieldNames.map(c => {
      const fieldName = c;
      const field = dataset.schema.fields.find(f => f.systemName === fieldName);
      if (!!field) {
        let cellRenderer: string;

        if (
          skipLinkChannels !== true &&
          (field.systemName === 'name' ||
            field.systemName === 'summary' ||
            field.systemName === 'subject' ||
            field.systemName === 'display_name' ||
            (dataset.type === DatasetType.Incident && field.systemName === 'number'))
        ) {
          cellRenderer = 'linkCellComponent';
        } else {
          cellRenderer = getCellRenderer(field.dataType);
        }

        const valueGetter = getValueGetter(field.dataType);

        const filter = getCellFilter(field.dataType);

        const sortColId = getCellSort(field);

        const column = {
          headerName: field.displayName,
          field: field.systemName,
          colId: `${field.systemName}`,
          sortColId: sortColId,
          enableRowGroup: true,
          filterParams: {
            buttons: ['reset', 'apply'],
            debounceMs: 1000,
            valueFormatter: filter === 'agSetColumnFilter' ? this.formatTextForTable : null,
            suppressAndOrCondition: true,
            filterOptions:
              filter === 'agSetColumnFilter'
                ? field.dataType === FieldDataType.Text || field.dataType === FieldDataType.Email
                  ? ['equals', 'notEqual', 'equalsNoCase', 'notEqualNoCase', 'contains', 'notContains', 'blank', 'notBlank']
                  : ['equals', 'notEqual', 'contains', 'notContains', 'blank', 'notBlank']
                : undefined
          },

          filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
          floatingFilter: false,
          sortable: true,
          resizable: true,
          minWidth: cellRenderer === 'phoneCellComponent' ? 240 : 150,
          menuTabs: ['filterMenuTab'],
          cellRenderer,
          cellRendererParams: {
            schemaField: field,
            organizationUId: organizationUId,
            sortFieldName: sortColId !== field.systemName ? sortColId : undefined,
            workflows: [workflow]
          },
          valueGetter
        };

        if (field.dataType === FieldDataType.Picklist || field.dataType === FieldDataType.MultiPicklist) {
          (column as any).keyCreator = params => {
            return this.getPicklistFieldValue(field, params);
          };
          (column as any).filter = 'agSetColumnFilter';
          (column.filterParams as any).values = this.getFieldPicklistValues(field);
          (column.filterParams as any).valueGetter = params => {
            return this.getPicklistFieldValue(field, params);
          };
        }
        if (field.dataType === FieldDataType.Enumeration) {
          (column as any).keyCreator = params => {
            return this.getEnumerationFieldValue(field, params);
          };
          (column as any).filter = 'agSetColumnFilter';
          (column.filterParams as any).values = this.getFieldEnumerationValues(field);
          (column.filterParams as any).valueGetter = params => {
            return this.getEnumerationFieldValue(field, params);
          };
        }

        columns.push(column);
      }
    });

    if (setDistanceColumn) {
      this.addDistanceColumn(columns);
    }

    return columns;
  }

  private getPicklistFieldValue(field: SchemaField, params: any): string {
    if (!!params?.data) {
      return field.picklistValues?.find(p => p.uId === params?.data[field.systemName])?.value;
    } else {
      return field.picklistValues?.find(p => p.uId === params?.value)?.value;
    }
  }

  private getFieldPicklistValues(field: SchemaField): string[] {
    let results = [null];
    results = results.concat(field.picklistValues?.map(p => p.value));
    return results;
  }

  private getEnumerationFieldValue(field: SchemaField, params: any): number {
    if (!!params?.data) {
      return field.enumerationValues?.find(p => p.value === params?.data[field.systemName])?.value;
    } else {
      return field.enumerationValues?.find(p => p.value === params?.value)?.value;
    }
  }

  private getFieldEnumerationValues(field: SchemaField): string[] {
    let results = [null];
    results = results.concat(field.enumerationValues?.map(p => p.name));
    return results;
  }

  formatTextForTable(params: ValueFormatterParams) {
    const text = params.value;
    if (text) {
      return text.replace(/(\r\n|\n|\r)/gm, '');
    }
    return text;
  }

  private addDistanceColumn(columns: any[]) {
    const distanceField = {
      dataType: FieldDataType.Distance,
      displayName: 'Distance',
      systemName: 'topoone_metadata_closest_distance',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    };

    const valueGetter = getValueGetter(distanceField.dataType);
    const filter = getCellFilter(distanceField.dataType);
    const cellRenderer = getCellRenderer(distanceField.dataType);
    const distanceColDef = {
      headerName: distanceField.displayName,
      field: distanceField.systemName,
      colId: `${distanceField.systemName}`,
      enableRowGroup: true,
      filter,
      filterParams: {
        buttons: ['reset'],
        valueFormatter: filter === 'agSetColumnFilter' ? this.formatTextForTable : null
      },
      floatingFilter: false,
      sortable: true,
      resizable: true,
      minWidth: 150,
      menuTabs: ['filterMenuTab'],
      cellRenderer,
      cellRendererParams: {
        schemaField: distanceField
      },
      valueGetter
    };
    if (columns.length > 4) {
      columns.splice(4, 0, distanceColDef);
    } else {
      columns.unshift();
    }
  }

  public getColumnFilterFromGrid(filterModel: any): FilterGroup {
    let columnFilter = null;

    if (!!filterModel) {
      const colFilter = {
        filterGroups: [],
        filters: [],
        groupType: FilterGroupType.And
      };

      for (const field in filterModel) {
        const ruleOp = this.getRuleOperatorFromOption(filterModel[field].type);
        const ruleValue = filterModel[field].filter;
        if (!!ruleOp) {
          colFilter.filters.push({
            field: field,
            operator: ruleOp,
            value: ruleValue,
            filterOptions: null
          });
        }
        columnFilter = colFilter;
      }
    }

    return columnFilter;
  }

  public getRuleOperatorFromOption(option: string): RuleOperator {
    return this.optionsToRuleOperators.find(o => o.option === option)?.operator;
  }
}
